import React, { useState, useEffect, useCallback } from 'react';
import { Check, X, Trash2, Search } from 'lucide-react';
import { FieldType } from '@frontend/app/types/globalTypes';
import { Button } from '@/shadcn/components/ui/button';
import { Card, CardContent } from '@/shadcn/components/ui/card';
import { Input } from '@/shadcn/components/ui/input';
import { Label } from '@/shadcn/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shadcn/components/ui/select';
import { CustomFieldUpdate, AvailableCustomField } from './types';
import { CustomFieldInput } from './CustomFieldInput';

interface CustomFieldInputRowProps {
  update: CustomFieldUpdate;
  availableFields: AvailableCustomField[];
  onUpdateField: (update: CustomFieldUpdate) => void;
  onSave: (savedUpdate?: CustomFieldUpdate) => void;
  onCancel: () => void;
  onDelete: () => void;
  isValid: boolean;
  usedFieldIds?: string[]; // List of field IDs already selected by other rows
}

export const CustomFieldInputRow: React.FC<CustomFieldInputRowProps> = ({
  update,
  availableFields,
  onUpdateField,
  onSave,
  onCancel,
  onDelete,
  isValid: _isValid, // Renamed to indicate it's intentionally unused
  usedFieldIds = [],
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFieldId, setSelectedFieldId] = useState(update.fieldId);
  const [fieldValue, setFieldValue] = useState(update.fieldValue);
  const [validationError, setValidationError] = useState<string>('');
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Filter available fields based on search query
  const filteredFields = availableFields.filter((field) =>
    field.displayName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get selected field details
  const selectedField = availableFields.find((field) => field.id === selectedFieldId);

  // Update validation when field or value changes - only show errors after user interaction
  useEffect(() => {
    if (!hasUserInteracted) {
      setValidationError('');
      return;
    }

    const isEmptyArray = (v: unknown) => Array.isArray(v) && v.length === 0;
    const requiresNonEmpty =
      selectedField?.attributeType !== FieldType.TEXT; // TEXT accepts empty strings

    if (!selectedFieldId) {
      setValidationError('Please select a field');
      return;
    }
    if (!selectedField) {
      setValidationError('Please select a field');
      return;
    }
    if (
      requiresNonEmpty &&
      (fieldValue === undefined ||
        fieldValue === null ||
        isEmptyArray(fieldValue) ||
        (fieldValue === '' && ![FieldType.TEXT].includes(selectedField.attributeType)))
    ) {
      setValidationError('Please enter a value');
      return;
    }
    setValidationError('');
  }, [selectedFieldId, fieldValue, selectedField, hasUserInteracted]);

  // Handle field selection - do NOT save to parent yet
  const handleFieldSelect = (fieldId: string) => {
    const field = availableFields.find((f) => f.id === fieldId);
    if (field) {
      // Check if this field is already used by another row
      if (usedFieldIds.includes(fieldId) && fieldId !== update.fieldId) {
        setValidationError('This field is already being updated by another rule. Please select a different field.');
        setHasUserInteracted(true);
        return;
      }
      setSelectedFieldId(fieldId);
      setFieldValue(''); // Reset value when field changes
      setHasUserInteracted(true);
      setValidationError(''); // Clear any previous validation error

      // Do NOT update parent - just store locally until save
    }
  };

  // Handle value change - store locally, never auto-save
  const handleValueChange = useCallback((value: any) => {
    setFieldValue(value);
    setHasUserInteracted(true);

    // Store locally only - no parent updates until explicit save
  }, []);

  // Handle save - validate everything then save to parent
  const handleSave = () => {
    // Step 1: Validate field is selected
    if (!selectedFieldId || !selectedField) {
      setValidationError('Please select a field');
      setHasUserInteracted(true);
      return;
    }

    // Step 2: Validate value based on field type
    const isValidValue = validateFieldValue(selectedField, fieldValue);
    if (!isValidValue) {
      // Provide specific error messages based on field type
      let errorMessage = 'Please enter a valid value';
      if (selectedField.attributeType === FieldType.TEXT) {
        if (selectedField.choices && selectedField.choices.length > 0) {
          errorMessage = 'Please select an option';
        } else {
          errorMessage = 'Please enter a value';
        }
      } else if (selectedField.attributeType === FieldType.ARRAY) {
        errorMessage = 'Please select at least one option';
      } else if (selectedField.attributeType === FieldType.DATE) {
        errorMessage = 'Please select a date';
      } else if (selectedField.attributeType === FieldType.NUMBER) {
        errorMessage = 'Please enter a valid number';
      } else if (selectedField.attributeType === FieldType.BOOLEAN) {
        errorMessage = 'Please select Yes or No';
      }
      setValidationError(errorMessage);
      setHasUserInteracted(true);
      return;
    }

    // Step 3: Create complete update object with proper fieldId from conditionOptions
    const completeUpdate: CustomFieldUpdate = {
      ...update,
      fieldId: selectedField.id, // Use the id from the selected field (e.g., "MEMBER_FIELD.145025")
      fieldValue,
      attributeType: selectedField.attributeType,
      displayName: selectedField.displayName,
      choices: selectedField.choices || undefined,
    };

    // Step 4: Save to parent and close editing - pass the complete update to onSave
    onUpdateField(completeUpdate);
    onSave(completeUpdate);
  };

  // Validate field value based on type
  const validateFieldValue = (field: AvailableCustomField, value: any): boolean => {
    // Check for undefined/null values
    if (value === undefined || value === null) {
      return false;
    }

    switch (field.attributeType) {
      case FieldType.DATE:
        return value instanceof Date || (typeof value === 'string' && value !== '' && !isNaN(Date.parse(value)));
      case FieldType.ARRAY:
        return Array.isArray(value) && value.length > 0;
      case FieldType.BOOLEAN:
        return typeof value === 'boolean';
      case FieldType.NUMBER:
        return typeof value === 'number' && !isNaN(value);
      case FieldType.TEXT:
      default:
        // For TEXT fields with choices (single select), require a selection
        if (field.choices && field.choices.length > 0) {
          return typeof value === 'string' && value !== '';
        }
        // For free text fields, require non-empty string
        return typeof value === 'string' && value.trim() !== '';
    }
  };

  return (
    <Card className="bg-white rounded-2xl border border-solid border-grey-2 shadow-none">
      <CardContent className="flex flex-col items-center justify-center gap-2 px-0 py-3">
        <div className="flex flex-col items-start gap-2 px-3 py-0 self-stretch w-full relative flex-[0_0_auto]">
          <div className="flex flex-col items-start gap-2 self-stretch w-full rounded-2xl relative flex-[0_0_auto]">

            {/* Field Selection Section */}
            <div className="flex flex-col items-start relative self-stretch w-full flex-[0_0_auto]">
              <div className="flex items-center gap-1 pt-0 pb-2 px-0 relative self-stretch w-full flex-[0_0_auto]">
                <Label className="relative w-fit mt-[-1.00px] font-medium text-grey-6 text-sm">
                  Select Field
                </Label>
              </div>

              <Select
                value={selectedFieldId}
                onValueChange={handleFieldSelect}
              >
                <SelectTrigger className="flex items-center gap-1 px-3 py-1 relative self-stretch w-full flex-[0_0_auto] bg-white rounded-lg border border-solid border-grey-2 h-auto">
                  <SelectValue
                    placeholder="Select Custom Field"
                    className="text-grey-6"
                  >
                    {selectedField && (
                      <div className="flex items-center">
                        <span className="text-grey-6">{selectedField.displayName}</span>
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="w-[300px]">
                  {/* Search Input */}
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-grey-4" />
                      <Input
                        placeholder="Search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-8"
                      />
                    </div>
                  </div>

                  {/* Field Options */}
                  {filteredFields.map((field) => (
                    <SelectItem key={field.id} value={field.id}>
                      <div className="flex items-center">
                        <span className="text-grey-6">{field.displayName} </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Value Input Section - Only show if field is selected */}
            {selectedField && (
              <div className="flex flex-col items-start relative self-stretch w-full flex-[0_0_auto]">
                <div className="flex items-center gap-1 pt-0 pb-2 px-0 relative self-stretch w-full flex-[0_0_auto]">
                  <Label className="relative w-fit mt-[-1.00px] font-medium text-grey-6 text-sm">
                    Update with
                  </Label>
                </div>

                <CustomFieldInput
                  field={selectedField}
                  value={fieldValue}
                  onChange={handleValueChange}
                  error={validationError}
                  placeholder="Enter value to update"
                />
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between px-3 py-0 self-stretch w-full relative flex-[0_0_auto]">
          <div className="inline-flex items-center gap-2 relative flex-[0_0_auto]">
            <Button
              variant="default"
              size="sm"
              onClick={handleSave}
              disabled={!selectedFieldId || !selectedField || (selectedField && !validateFieldValue(selectedField, fieldValue))}
              className="w-6 h-6 bg-grey-6 hover:bg-grey-6 text-white p-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="w-3 h-3" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              className="w-6 h-6 bg-white hover:bg-white text-grey-6 p-1.5 rounded-lg border-grey-2"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            className="w-6 h-6 bg-white hover:bg-white text-red-500 p-1 rounded-lg border-red-500"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
